import { CalendarTodayOutlined } from "@mui/icons-material";
import { Box, Button, IconButton, Menu, useTheme } from "@mui/material";
import { FC, useMemo } from "react";
import { ConfirmationDialog } from "../../../../components/ConfirmationDialog";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { useLocalization } from "../../../../hooks/useLocalization";

interface TimeFilterWrapperProps {
  children?: React.ReactNode;
  handleTimeFilterOpen: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleTimeFilterClose: () => void;
  timeFilterAnchor: HTMLElement | null;
  onApply: () => void;
  isApplyDisabled: boolean;
  isTimeFilterOpen: boolean;
}

const MenuContent: FC<TimeFilterWrapperProps> = ({
  onApply,
  isApplyDisabled,
  children,
  timeFilterAnchor,
  handleTimeFilterClose,
  isTimeFilterOpen,
}) => {
  const { t } = useLocalization();

  return (
    <Menu
      MenuListProps={{
        sx: {
          width: "100%",
          padding: 0,
        },
      }}
      anchorEl={timeFilterAnchor}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      onClose={handleTimeFilterClose}
      open={isTimeFilterOpen}
      slotProps={{
        paper: {
          sx: {
            width: 500,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderRadius: "24px",
          },
        },
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
    >
      {[
        children,
        <Box key="apply-btn-box" sx={{ p: "16px" }}>
          <Button
            disabled={isApplyDisabled}
            fullWidth
            onClick={onApply}
            variant="contained"
          >
            {t("pages.dataAnalytics.timeFilter.apply")}
          </Button>
        </Box>,
      ]}
    </Menu>
  );
};

const DialogContent: FC<TimeFilterWrapperProps> = ({
  onApply,
  isApplyDisabled,
  children,
  handleTimeFilterClose,
  isTimeFilterOpen,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();

  return (
    <ConfirmationDialog.Root
      fullScreenOnMobile
      maxWidth="xs"
      onClose={handleTimeFilterClose}
      open={isTimeFilterOpen}
    >
      <ConfirmationDialog.Content>{children}</ConfirmationDialog.Content>
      <ConfirmationDialog.Actions>
        <ConfirmationDialog.Button
          onClick={handleTimeFilterClose}
          sx={{
            color: theme.colors.primary[5],
          }}
          variant="secondary"
        >
          {t("pages.dataAnalytics.timeFilter.cancel")}
        </ConfirmationDialog.Button>
        <ConfirmationDialog.Button
          disabled={isApplyDisabled}
          onClick={onApply}
          variant="contained"
        >
          {t("pages.dataAnalytics.timeFilter.apply")}
        </ConfirmationDialog.Button>
      </ConfirmationDialog.Actions>
    </ConfirmationDialog.Root>
  );
};

export const TimeFilterWrapper: FC<TimeFilterWrapperProps> = ({
  children,
  handleTimeFilterOpen,
  handleTimeFilterClose,
  timeFilterAnchor,
  onApply,
  isApplyDisabled,
  isTimeFilterOpen,
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();

  const Wrapper = useMemo(
    () => (isMobile ? DialogContent : MenuContent),
    [isMobile],
  );

  return (
    <>
      <IconButton
        onClick={handleTimeFilterOpen}
        sx={{
          backgroundColor: theme.colors.grey,
          borderRadius: "8px",
          color: theme.colors.primary[5],
          height: 40,
          width: 40,
        }}
      >
        <CalendarTodayOutlined sx={{ fontSize: 20 }} />
      </IconButton>
      <Wrapper
        handleTimeFilterClose={handleTimeFilterClose}
        handleTimeFilterOpen={handleTimeFilterOpen}
        isApplyDisabled={isApplyDisabled}
        isTimeFilterOpen={isTimeFilterOpen}
        onApply={onApply}
        timeFilterAnchor={timeFilterAnchor}
      >
        {children}
      </Wrapper>
    </>
  );
};
